import React, { useState } from 'react';
import { X, Plus, Trash2, Save, Type, Image, FileText, MousePointer2, Smartphone, Layout, Book, ChevronRight, Wand2, Upload, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { cn } from '../../utils/cn';
import api from '../../services/api';
import alertService from '../../utils/alert';

// Every {{n}} placeholder used in a piece of text, in ascending order — used
// both to render one sample-value input per variable and to know how many
// example values Meta requires before it will accept the template.
const variableNumbers = (text) => {
  const nums = new Set([...(text || '').matchAll(/\{\{(\d+)\}\}/g)].map((m) => Number(m[1])));
  return [...nums].sort((a, b) => a - b);
};

const MetaTemplateBuilder = ({ onClose, onSave, initialTemplate = null, editMode = false }) => {
  const [loading, setLoading] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [headerMediaName, setHeaderMediaName] = useState('');
  const [template, setTemplate] = useState(() => initialTemplate ? {
    name: initialTemplate.name,
    category: initialTemplate.category,
    language: initialTemplate.language,
    components: initialTemplate.components || []
  } : {
    name: '',
    category: 'MARKETING',
    language: 'en_US',
    components: [
      { type: 'BODY', text: 'Hello {{1}}, welcome to our service!' }
    ]
  });

  const [activeSection, setActiveSection] = useState('BODY');
  const [activeTab, setActiveTab] = useState('editor'); // editor | library

  const categories = [
    { value: 'MARKETING', label: 'Marketing', desc: 'Promotions, offers, and informational updates.' },
    { value: 'UTILITY', label: 'Utility', desc: 'Confirmations, receipts, and order updates.' },
    { value: 'AUTHENTICATION', label: 'Authentication', desc: 'One-time passwords and security codes.' }
  ];

  const libraryTemplates = [
    {
      name: 'welcome_message',
      category: 'MARKETING',
      components: [
        { type: 'HEADER', format: 'TEXT', text: 'Welcome to {{1}}!' },
        { type: 'BODY', text: 'Hi {{2}}, thank you for joining us. We are excited to have you on board!' },
        { type: 'FOOTER', text: 'Reply STOP to unsubscribe' }
      ]
    },
    {
      name: 'order_confirmation',
      category: 'UTILITY',
      components: [
        { type: 'HEADER', format: 'TEXT', text: 'Order Confirmed' },
        { type: 'BODY', text: 'Your order #{{1}} has been received and is being processed. Expected delivery: {{2}}.' },
        { type: 'BUTTONS', buttons: [{ type: 'URL', text: 'Track Order', url: 'https://example.com/track' }] }
      ]
    },
    {
      name: 'appointment_reminder',
      category: 'UTILITY',
      components: [
        { type: 'BODY', text: 'Friendly reminder of your appointment on {{1}} at {{2}}.' },
        { type: 'BUTTONS', buttons: [{ type: 'QUICK_REPLY', text: 'Confirm Attendance' }, { type: 'QUICK_REPLY', text: 'Reschedule' }] }
      ]
    }
  ];

  const addComponent = (type) => {
    if (template.components.some(c => c.type === type) && type !== 'BUTTONS') return;

    let newComponent = { type };
    if (type === 'HEADER') newComponent.format = 'TEXT', newComponent.text = '';
    if (type === 'FOOTER') newComponent.text = '';
    if (type === 'BUTTONS') newComponent.buttons = [];

    setTemplate({ ...template, components: [...template.components, newComponent] });
    setActiveSection(type);
  };

  const updateComponent = (type, data) => {
    setTemplate({
      ...template,
      components: template.components.map(c => c.type === type ? { ...c, ...data } : c)
    });
  };

  // Always inserted the literal "{{1}}" regardless of what's already in the
  // body — clicking this twice produced "...{{1}} {{1}}" instead of
  // "...{{1}} {{2}}". WhatsApp requires template variables to be
  // sequential/unique; a duplicated {{1}} either gets rejected by Meta's
  // review or, worse, silently binds two different pieces of content to the
  // same substitution value at send time.
  const insertNextVariable = () => {
    const bodyText = template.components.find(c => c.type === 'BODY')?.text || '';
    const usedNumbers = [...bodyText.matchAll(/\{\{(\d+)\}\}/g)].map(m => Number(m[1]));
    const nextNumber = usedNumbers.length ? Math.max(...usedNumbers) + 1 : 1;
    updateComponent('BODY', { text: `${bodyText} {{${nextNumber}}}` });
  };

  const handleAddButton = () => {
    const buttonsComp = template.components.find(c => c.type === 'BUTTONS') || { type: 'BUTTONS', buttons: [] };
    if (buttonsComp.buttons.length >= 3) return;

    const newButton = { type: 'QUICK_REPLY', text: 'New Button' };
    const newButtons = [...buttonsComp.buttons, newButton];

    if (template.components.some(c => c.type === 'BUTTONS')) {
      updateComponent('BUTTONS', { buttons: newButtons });
    } else {
      setTemplate({ ...template, components: [...template.components, { type: 'BUTTONS', buttons: newButtons }] });
    }
  };

  // Meta requires a real uploaded media handle (not a URL) as the sample for
  // an IMAGE/VIDEO/DOCUMENT header, via its separate Resumable Upload API —
  // previously the builder never called this at all and just showed a
  // "Media Placeholder" with no actual upload, guaranteeing rejection for
  // any non-TEXT header. Uploads immediately on file selection so the
  // returned handle (`example.header_handle`) is already in place before submit.
  const handleHeaderMediaUpload = async (file) => {
    if (!file) return;
    setUploadingMedia(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/meta/templates/example-media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateComponent('HEADER', { example: { header_handle: [data.headerHandle] } });
      setHeaderMediaName(file.name);
    } catch (err) {
      const d = err.response?.data || {};
      alertService.errorDetails('Upload Failed', d.error || 'Could not upload the sample media file.', {
        code: d.code, subcode: d.subcode, type: d.type, fbtraceId: d.fbtraceId,
      });
    } finally {
      setUploadingMedia(false);
    }
  };

  // Mirrors the server-side check (MetaTemplateService.validateTemplatePayload)
  // so an incomplete template gets caught here with an inline message instead
  // of a round trip that just surfaces the same gap after a submit attempt.
  const validateBeforeSubmit = () => {
    for (const comp of template.components) {
      if (comp.type === 'HEADER') {
        if (comp.format === 'TEXT') {
          const vars = variableNumbers(comp.text);
          if (vars.length && (comp.example?.header_text || []).some((v) => !v)) {
            return 'Fill in a sample value for the header variable before submitting.';
          }
        } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(comp.format) && !comp.example?.header_handle?.length) {
          return `Upload a sample ${comp.format.toLowerCase()} file for the header before submitting.`;
        }
      }
      if (comp.type === 'BODY' && template.category !== 'AUTHENTICATION') {
        const vars = variableNumbers(comp.text);
        const samples = comp.example?.body_text?.[0] || [];
        if (vars.some((_, i) => !samples[i])) {
          return 'Fill in a sample value for every body variable before submitting.';
        }
      }
      if (comp.type === 'BUTTONS') {
        for (const btn of comp.buttons || []) {
          if (btn.type === 'URL' && !btn.url) return `The "${btn.text || 'URL'}" button needs a destination URL.`;
          if (btn.type === 'PHONE_NUMBER' && !btn.phone_number) return `The "${btn.text || 'Phone'}" button needs a phone number.`;
          if (btn.type === 'OTP' && ['ONE_TAP', 'ZERO_TAP'].includes(btn.otp_type) && (!btn.package_name || !btn.signature_hash)) {
            return `${btn.otp_type} needs your Android app's package name and signature hash before submitting.`;
          }
        }
      }
    }
    return null;
  };

  const handleSave = async () => {
    if (!template.name) return alertService.error('Error', 'Template name is required');
    const validationError = validateBeforeSubmit();
    if (validationError) return alertService.error('Incomplete Template', validationError);
    setLoading(true);
    try {
      await onSave(template);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const useLibraryTemplate = (lib) => {
    setTemplate({
      ...template,
      category: lib.category,
      components: lib.components
    });
    setActiveTab('editor');
    setActiveSection('BODY');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-0 lg:p-6 overflow-hidden">
      <div className="bg-[#F4F6F9] w-full max-w-[1400px] h-full lg:h-[90vh] rounded-none lg:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] flex flex-col border border-[#E5E5E5]">

        {/* Header */}
        <div className="h-20 px-8 flex items-center justify-between bg-white border-b border-[#D8DDE6] shrink-0">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#0176D3] rounded-lg text-white shadow-[0_2px_8px_rgba(0,0,0,0.16)] ">
              <Layout className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-[18px] font-bold text-[#080707] tracking-tight">{editMode ? `Edit "${template.name}"` : 'Meta Template Architect'}</h2>
              <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-[0.2em]">{editMode ? 'Editing re-submits this template for Meta review' : 'Enterprise Messaging Registry'}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 p-1 bg-[#F4F6F9] rounded-lg border border-[#D8DDE6]">
            {[
              { id: 'editor', label: 'Visual Editor', icon: Wand2 },
              ...(editMode ? [] : [{ id: 'library', label: 'Template Library', icon: Book }]),
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-2 px-6 py-2 rounded-lg text-[12px] font-bold transition-all",
                  activeTab === tab.id ? "bg-white text-[#0176D3] shadow-[0_2px_8px_rgba(0,0,0,0.16)]" : "text-[#706E6B] hover:text-[#080707]"
                )}
              >
                <tab.icon size={14} />
                {tab.label}
              </button>
            ))}
          </div>

          <button onClick={onClose} className="p-2 hover:bg-[#F4F6F9] rounded transition-colors border border-transparent hover:border-[#D8DDE6]">
            <X className="w-6 h-6 text-[#706E6B]" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">

          {activeTab === 'editor' ? (
            <>
              {/* Left Panel: Config */}
              <div className="w-full sm:w-[400px] border-r border-[#D8DDE6] bg-white overflow-y-auto no-scrollbar p-8 space-y-10">
                <div className="space-y-4">
                  <label className="text-[11px] font-bold text-[#706E6B] tracking-[0.2em] block">Template Identity</label>
                  <div className="relative group">
                    <input
                      type="text"
                      placeholder="seasonal_offer_2024"
                      value={template.name}
                      disabled={editMode}
                      onChange={(e) => setTemplate({ ...template, name: e.target.value.toLowerCase().replace(/ /g, '_') })}
                      className="w-full px-5 py-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg text-[14px] font-bold outline-none focus:bg-white focus:border-[#0176D3] focus:ring-4 focus:ring-[#0176D3]/5 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#0176D3] animate-pulse" />
                  </div>
                  <p className="text-[11px] text-[#706E6B] font-medium leading-relaxed italic">
                    {editMode ? "* Meta doesn't allow renaming an existing template — only its content below." : '* Use lowercase letters, numbers and underscores only.'}
                  </p>
                </div>

                <div className="space-y-4">
                  <label className="text-[11px] font-bold text-[#706E6B] tracking-[0.2em] block">Primary Category</label>
                  <div className={cn("space-y-3", editMode && "opacity-60 pointer-events-none")}>
                    {categories.map(cat => (
                      <div
                        key={cat.value}
                        onClick={() => {
                          if (editMode) return;
                          if (cat.value === template.category) return;
                          if (cat.value === 'AUTHENTICATION') {
                            setTemplate({
                              ...template,
                              category: cat.value,
                              components: [
                                { type: 'BODY', add_security_recommendation: false },
                                { type: 'FOOTER', code_expiration_minutes: 10 },
                                { type: 'BUTTONS', buttons: [{ type: 'OTP', otp_type: 'COPY_CODE', text: 'Copy Code' }] }
                              ]
                            });
                          } else if (template.category === 'AUTHENTICATION') {
                            setTemplate({
                              ...template,
                              category: cat.value,
                              components: [{ type: 'BODY', text: 'Hello {{1}}, welcome to our service!' }]
                            });
                          } else {
                            setTemplate({ ...template, category: cat.value });
                          }
                          setActiveSection('BODY');
                        }}
                        className={cn(
"p-5 border-2 rounded-lg cursor-pointer transition-all relative overflow-hidden group",
                          template.category === cat.value ? "bg-[#0176D3]/5 border-[#0176D3]" : "bg-white border-[#EEF1F6] hover:border-[#D8DDE6]"
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className={cn("text-[14px] font-bold", template.category === cat.value ? "text-[#0176D3]" : "text-[#080707]")}>{cat.label}</h4>
                          {template.category === cat.value && <div className="w-5 h-5 rounded-full bg-[#0176D3] flex items-center justify-center text-white"><ChevronRight size={12} /></div>}
                        </div>
                        <p className="text-[12px] text-[#706E6B] font-medium leading-relaxed">{cat.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="text-[11px] font-bold text-[#706E6B] tracking-[0.2em] block">Regional Language</label>
                  <select
                    className="w-full px-5 py-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[14px] font-bold outline-none focus:bg-white focus:border-[#0176D3] transition-all appearance-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    value={template.language}
                    disabled={editMode}
                    onChange={(e) => setTemplate({ ...template, language: e.target.value })}
                  >
                    <option value="en_US">🇺🇸 English (US)</option>
                    <option value="es_ES">🇪🇸 Spanish (ES)</option>
                    <option value="pt_BR">🇧🇷 Portuguese (BR)</option>
                    <option value="hi_IN">🇮🇳 Hindi (IN)</option>
                  </select>
                </div>
              </div>

              {/* Middle Panel: Editor */}
              <div className="flex-1 bg-white border-r border-[#D8DDE6] flex flex-col min-w-0">
                <div className="h-20 px-8 border-b border-[#D8DDE6] flex items-center gap-4 bg-[#F4F6F9]/30 overflow-x-auto no-scrollbar shrink-0">
                  {[
                    { type: 'HEADER', icon: Type, label: 'Header' },
                    { type: 'BODY', icon: FileText, label: 'Body' },
                    { type: 'FOOTER', icon: Type, label: 'Footer' },
                    { type: 'BUTTONS', icon: MousePointer2, label: 'Buttons' },
                  ].filter(sec => template.category !== 'AUTHENTICATION' || sec.type !== 'HEADER').map(sec => (
                    <button
                      key={sec.type}
                      onClick={() => {
                        if (!template.components.some(c => c.type === sec.type)) {
                          addComponent(sec.type);
                        } else {
                          setActiveSection(sec.type);
                        }
                      }}
                      className={cn(
"flex items-center gap-3 px-6 py-2.5 rounded-lg text-[12px] font-bold uppercase tracking-[0.1em] transition-all whitespace-nowrap border-2",
                        activeSection === sec.type
                          ? "bg-white border-[#0176D3] text-[#0176D3] shadow-[0_2px_8px_rgba(0,0,0,0.16)] "
                          : "bg-transparent border-transparent text-[#706E6B] hover:text-[#080707] hover:bg-white/50"
                      )}
                    >
                      <sec.icon size={16} />
                      {sec.label}
                      {template.components.some(c => c.type === sec.type) && <div className="w-2 h-2 rounded-full bg-[#04844B] shadow-[0_0_8px_rgba(34,197,94,0.5)]" />}
                    </button>
                  ))}
                </div>

                <div className="flex-1 p-12 overflow-y-auto no-scrollbar">
                  <div className="max-w-3xl mx-auto">
                    {/* Render active section content */}
                    {activeSection === 'HEADER' && (
                      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
                        <div className="flex items-center justify-between bg-[#F4F6F9] p-6 rounded-lg border border-[#D8DDE6]">
                          <div>
                            <h3 className="text-[15px] font-bold text-[#080707]">Header Format</h3>
                            <p className="text-[12px] text-[#706E6B] font-medium">Select how your message starts.</p>
                          </div>
                          <div className="flex gap-2">
                            {['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'].map(fmt => (
                              <button
                                key={fmt}
                                onClick={() => {
                                  // Clear the other format's leftover state — a stale `text`
                                  // on a media header, or a stale `header_handle` if
                                  // switching back to TEXT, would otherwise ride along.
                                  setHeaderMediaName('');
                                  updateComponent('HEADER', fmt === 'TEXT' ? { format: fmt, text: '', example: undefined } : { format: fmt, text: undefined, example: undefined });
                                }}
                                className={cn(
                                  "px-4 py-2 rounded-lg text-[11px] font-bold uppercase transition-all",
                                  template.components.find(c => c.type === 'HEADER')?.format === fmt ? "bg-[#0176D3] text-white" : "bg-white text-[#706E6B] border border-[#D8DDE6]"
                                )}
                              >
                                {fmt}
                              </button>
                            ))}
                          </div>
                        </div>

                        {template.components.find(c => c.type === 'HEADER')?.format === 'TEXT' ? (
                          <div className="space-y-6">
                            <div className="space-y-4">
                               <label className="text-[11px] font-bold text-[#706E6B] tracking-[0.2em]">Header Text</label>
                               <input
                                type="text"
                                value={template.components.find(c => c.type === 'HEADER').text}
                                onChange={(e) => updateComponent('HEADER', { text: e.target.value })}
                                placeholder="Enter header text... Use {{1}} for a variable."
                                className="w-full px-6 py-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg text-[15px] font-bold outline-none focus:bg-white focus:border-[#0176D3] transition-all"
                              />
                            </div>
                            {/* Meta rejects a variable header with no example value — one
                                sample input per {{n}} found in the text (header supports at
                                most one variable, but this scales if that ever changes). */}
                            {variableNumbers(template.components.find(c => c.type === 'HEADER')?.text).map((n, i) => (
                              <div key={n} className="space-y-2">
                                <label className="text-[11px] font-bold text-[#706E6B] tracking-[0.2em]">Sample value for {'{{' + n + '}}'}</label>
                                <input
                                  type="text"
                                  value={template.components.find(c => c.type === 'HEADER')?.example?.header_text?.[i] || ''}
                                  onChange={(e) => {
                                    const header = template.components.find(c => c.type === 'HEADER');
                                    const current = [...(header.example?.header_text || [])];
                                    current[i] = e.target.value;
                                    updateComponent('HEADER', { example: { ...header.example, header_text: current } });
                                  }}
                                  placeholder="e.g. Priya"
                                  className="w-full px-6 py-3 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg text-[14px] font-medium outline-none focus:bg-white focus:border-[#0176D3] transition-all"
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="p-10 border-2 border-dashed border-[#D8DDE6] rounded-lg text-center bg-[#F4F6F9]/30">
                              <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mx-auto mb-6 shadow-sm text-[#706E6B]">
                                {uploadingMedia ? <Loader2 size={32} className="animate-spin" /> : template.components.find(c => c.type === 'HEADER')?.example?.header_handle?.length ? <CheckCircle2 size={32} className="text-[#04844B]" /> : template.components.find(c => c.type === 'HEADER')?.format === 'IMAGE' ? <Image size={32} /> : <FileText size={32} />}
                              </div>
                              <h4 className="text-[16px] font-bold text-[#080707] mb-2">
                                {headerMediaName || (template.components.find(c => c.type === 'HEADER')?.example?.header_handle?.length ? 'Sample uploaded' : 'Upload a sample file')}
                              </h4>
                              <p className="text-[13px] text-[#706E6B] font-medium max-w-xs mx-auto leading-relaxed mb-6">
                                Meta requires a real sample {template.components.find(c => c.type === 'HEADER')?.format?.toLowerCase()} to review the template — this is only used for review, not sent to real recipients.
                              </p>
                              <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0176D3] text-white rounded-lg text-[12px] font-bold cursor-pointer hover:bg-[#0164B5] transition-all">
                                <Upload size={14} />
                                {uploadingMedia ? 'Uploading…' : 'Choose File'}
                                <input
                                  type="file"
                                  className="hidden"
                                  disabled={uploadingMedia}
                                  accept={template.components.find(c => c.type === 'HEADER')?.format === 'IMAGE' ? 'image/*' : template.components.find(c => c.type === 'HEADER')?.format === 'VIDEO' ? 'video/*' : '.pdf,.doc,.docx'}
                                  onChange={(e) => handleHeaderMediaUpload(e.target.files?.[0])}
                                />
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {activeSection === 'BODY' && (
                      template.category === 'AUTHENTICATION' ? (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
                          <div className="p-6 bg-[#F4F6F9] rounded-lg border border-[#D8DDE6] space-y-4">
                            <p className="text-[13px] text-[#706E6B] font-medium leading-relaxed">
                              Meta generates the verification code message automatically for Authentication templates — custom body text isn't allowed.
                            </p>
                            <label className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!template.components.find(c => c.type === 'BODY')?.add_security_recommendation}
                                onChange={(e) => updateComponent('BODY', { add_security_recommendation: e.target.checked })}
                                className="w-4 h-4"
                              />
                              <span className="text-[13px] font-bold text-[#080707]">Add security recommendation ("For your security, do not share this code.")</span>
                            </label>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
                           <div className="flex items-center justify-between mb-2">
                             <label className="text-[11px] font-bold text-[#706E6B] tracking-[0.2em]">Core Message Body</label>
                             <Button size="sm" variant="outline" onClick={insertNextVariable}>
                               <Plus size={14} className="mr-2" /> Insert Variable
                             </Button>
                           </div>
                           <textarea
                            rows={10}
                            value={template.components.find(c => c.type === 'BODY')?.text}
                            onChange={(e) => updateComponent('BODY', { text: e.target.value })}
                            placeholder="Architect your message content here... Use {{1}} for variables."
                            className="w-full px-8 py-8 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg text-[16px] font-medium text-[#080707] outline-none focus:bg-white focus:border-[#0176D3] focus:ring-8 focus:ring-[#0176D3]/5 transition-all resize-none leading-[1.6]"
                          />
                          {/* Meta rejects a body with variables and no example.body_text —
                              one sample input per {{n}} actually found in the body text. */}
                          {variableNumbers(template.components.find(c => c.type === 'BODY')?.text).length > 0 && (
                            <div className="space-y-3 p-6 bg-[#F4F6F9] rounded-lg border border-[#D8DDE6]">
                              <label className="text-[11px] font-bold text-[#706E6B] tracking-[0.2em] block">Sample Values (shown to Meta's reviewer)</label>
                              {variableNumbers(template.components.find(c => c.type === 'BODY')?.text).map((n, i) => (
                                <div key={n} className="flex items-center gap-3">
                                  <span className="text-[12px] font-bold text-[#0176D3] w-14 shrink-0">{'{{' + n + '}}'}</span>
                                  <input
                                    type="text"
                                    value={template.components.find(c => c.type === 'BODY')?.example?.body_text?.[0]?.[i] || ''}
                                    onChange={(e) => {
                                      const body = template.components.find(c => c.type === 'BODY');
                                      const current = [...(body.example?.body_text?.[0] || [])];
                                      current[i] = e.target.value;
                                      updateComponent('BODY', { example: { ...body.example, body_text: [current] } });
                                    }}
                                    placeholder={`e.g. ${['Priya', 'ORD-4821', 'Tuesday'][i] || 'sample value'}`}
                                    className="flex-1 px-4 py-2.5 bg-white border border-[#D8DDE6] rounded-lg text-[13px] font-medium outline-none focus:border-[#0176D3] transition-all"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    )}

                    {activeSection === 'FOOTER' && (
                      template.category === 'AUTHENTICATION' ? (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
                           <label className="text-[11px] font-bold text-[#706E6B] tracking-[0.2em] block">Code Expiration (minutes)</label>
                           <input
                            type="number"
                            min={1}
                            max={90}
                            value={template.components.find(c => c.type === 'FOOTER')?.code_expiration_minutes ?? 10}
                            onChange={(e) => updateComponent('FOOTER', { code_expiration_minutes: Math.min(90, Math.max(1, Number(e.target.value) || 1)) })}
                            className="w-full px-6 py-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg text-[14px] font-medium outline-none focus:bg-white focus:border-[#0176D3] transition-all"
                          />
                          <p className="text-[11px] text-[#706E6B] font-medium italic">Meta appends "This code expires in X minutes." automatically — custom footer text isn't allowed.</p>
                        </div>
                      ) : (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
                           <label className="text-[11px] font-bold text-[#706E6B] tracking-[0.2em] block">Footer Metadata</label>
                           <input
                            type="text"
                            value={template.components.find(c => c.type === 'FOOTER')?.text}
                            onChange={(e) => updateComponent('FOOTER', { text: e.target.value })}
                            placeholder="e.g. Reply STOP to unsubscribe"
                            className="w-full px-6 py-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg text-[14px] font-medium outline-none focus:bg-white focus:border-[#0176D3] transition-all"
                          />
                        </div>
                      )
                    )}

                    {activeSection === 'BUTTONS' && (
                      template.category === 'AUTHENTICATION' ? (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
                          <div className="p-8 bg-[#0176D3]/5 rounded-lg border border-[#0176D3]/10">
                            <h3 className="text-[16px] font-bold text-[#080707] mb-1">OTP Delivery Method</h3>
                            <p className="text-[13px] text-[#706E6B] font-medium">Authentication templates support exactly one OTP button.</p>
                          </div>
                          {(() => {
                            const otpButton = template.components.find(c => c.type === 'BUTTONS')?.buttons?.[0] || { type: 'OTP', otp_type: 'COPY_CODE', text: 'Copy Code' };
                            const updateOtpButton = (data) => updateComponent('BUTTONS', { buttons: [{ ...otpButton, ...data }] });
                            return (
                              <Card className="p-6 space-y-4 border-[#D8DDE6] shadow-none">
                                <div>
                                  <label className="text-[10px] font-bold text-[#706E6B] mb-1 block">OTP Type</label>
                                  <select
                                    className="w-full bg-[#F4F6F9] rounded px-3 py-2 text-[13px] font-bold outline-none"
                                    value={otpButton.otp_type}
                                    onChange={(e) => updateOtpButton({ otp_type: e.target.value })}
                                  >
                                    <option value="COPY_CODE">Copy Code</option>
                                    <option value="ONE_TAP">One-Tap Autofill</option>
                                    <option value="ZERO_TAP">Zero-Tap Autofill</option>
                                  </select>
                                </div>
                                {otpButton.otp_type === 'COPY_CODE' && (
                                  <div>
                                    <label className="text-[10px] font-bold text-[#706E6B] mb-1 block">Button Text</label>
                                    <input
                                      type="text"
                                      value={otpButton.text}
                                      onChange={(e) => updateOtpButton({ text: e.target.value })}
                                      className="w-full bg-transparent border-b border-[#D8DDE6] py-1 text-[14px] font-bold outline-none focus:border-[#0176D3]"
                                    />
                                  </div>
                                )}
                                {otpButton.otp_type !== 'COPY_CODE' && (
                                  <div className="space-y-3">
                                    <p className="text-[11px] text-[#706E6B] font-medium italic">
                                      {otpButton.otp_type} requires your Android app's identity — Meta rejects the template without both.
                                    </p>
                                    <div>
                                      <label className="text-[10px] font-bold text-[#706E6B] mb-1 block">Package Name</label>
                                      <input
                                        type="text"
                                        value={otpButton.package_name || ''}
                                        onChange={(e) => updateOtpButton({ package_name: e.target.value })}
                                        placeholder="com.yourcompany.app"
                                        className="w-full bg-[#F4F6F9] rounded-lg px-3 py-2 text-[13px] font-bold outline-none focus:bg-white border border-[#D8DDE6] focus:border-[#0176D3] transition-all"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-[10px] font-bold text-[#706E6B] mb-1 block">App Signature Hash</label>
                                      <input
                                        type="text"
                                        value={otpButton.signature_hash || ''}
                                        onChange={(e) => updateOtpButton({ signature_hash: e.target.value })}
                                        placeholder="e.g. A1B2C3D4E5F"
                                        className="w-full bg-[#F4F6F9] rounded-lg px-3 py-2 text-[13px] font-bold outline-none focus:bg-white border border-[#D8DDE6] focus:border-[#0176D3] transition-all"
                                      />
                                    </div>
                                  </div>
                                )}
                              </Card>
                            );
                          })()}
                        </div>
                      ) : (
                      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
                         <div className="flex items-center justify-between p-8 bg-[#0176D3]/5 rounded-lg border border-[#0176D3]/10">
                           <div>
                            <h3 className="text-[16px] font-bold text-[#080707]">Interactive Signals</h3>
                            <p className="text-[13px] text-[#706E6B] font-medium">Add up to 3 quick-action buttons.</p>
                           </div>
                           <Button onClick={handleAddButton} disabled={(template.components.find(c => c.type === 'BUTTONS')?.buttons?.length || 0) >= 3}>
                            <Plus size={16} className="mr-2" /> Add Component
                           </Button>
                         </div>

                         <div className="grid grid-cols-1 gap-4">
                           {template.components.find(c => c.type === 'BUTTONS')?.buttons?.map((btn, idx) => (
                             <Card key={idx} className="p-6 flex items-center gap-6 border-[#D8DDE6] group shadow-none hover:shadow-[0_2px_8px_rgba(0,0,0,0.16)] transition-all">
                               <div className="w-12 h-12 rounded-lg bg-[#F4F6F9] flex items-center justify-center text-[#706E6B] group-hover:bg-[#0176D3] group-hover:text-white transition-all">
                                 {btn.type === 'QUICK_REPLY' ? <Smartphone size={20} /> : <MousePointer2 size={20} />}
                               </div>
                               <div className="flex-1 space-y-4">
                                  <div className="flex gap-4">
                                    <div className="flex-1">
                                      <label className="text-[10px] font-bold text-[#706E6B] mb-1 block">Button Text</label>
                                      <input
                                        type="text"
                                        value={btn.text}
                                        onChange={(e) => {
                                          const newBtns = [...template.components.find(c => c.type === 'BUTTONS').buttons];
                                          newBtns[idx] = { ...btn, text: e.target.value };
                                          updateComponent('BUTTONS', { buttons: newBtns });
                                        }}
                                        className="w-full bg-transparent border-b border-[#D8DDE6] py-1 text-[14px] font-bold outline-none focus:border-[#0176D3]"
                                      />
                                    </div>
                                    <div className="w-40">
                                      <label className="text-[10px] font-bold text-[#706E6B] mb-1 block">Action Type</label>
                                      <select
                                        className="w-full bg-[#F4F6F9] rounded px-3 py-1.5 text-[12px] font-bold outline-none"
                                        value={btn.type}
                                        onChange={(e) => {
                                          const newBtns = [...template.components.find(c => c.type === 'BUTTONS').buttons];
                                          // Clear the other type's fields so a leftover url/phone_number
                                          // from a previous type never rides along in the submitted payload.
                                          newBtns[idx] = { type: e.target.value, text: btn.text };
                                          updateComponent('BUTTONS', { buttons: newBtns });
                                        }}
                                      >
                                        <option value="QUICK_REPLY">Quick Reply</option>
                                        <option value="URL">Open URL</option>
                                        <option value="PHONE_NUMBER">Call Number</option>
                                      </select>
                                    </div>
                                  </div>
                                  {/* URL/PHONE_NUMBER each need a required field Meta will otherwise
                                      reject the template for missing entirely — previously not
                                      rendered at all, so choosing either type left it incomplete. */}
                                  {btn.type === 'URL' && (
                                    <div className="space-y-3">
                                      <div>
                                        <label className="text-[10px] font-bold text-[#706E6B] mb-1 block">Destination URL</label>
                                        <input
                                          type="text"
                                          value={btn.url || ''}
                                          onChange={(e) => {
                                            const newBtns = [...template.components.find(c => c.type === 'BUTTONS').buttons];
                                            newBtns[idx] = { ...btn, url: e.target.value };
                                            updateComponent('BUTTONS', { buttons: newBtns });
                                          }}
                                          placeholder="https://example.com/orders/{{1}}"
                                          className="w-full bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg px-3 py-2 text-[13px] font-medium outline-none focus:border-[#0176D3] transition-all"
                                        />
                                      </div>
                                      {variableNumbers(btn.url).length > 0 && (
                                        <div>
                                          <label className="text-[10px] font-bold text-[#706E6B] mb-1 block">Sample value for {'{{' + variableNumbers(btn.url)[0] + '}}'}</label>
                                          <input
                                            type="text"
                                            value={btn.example?.[0] || ''}
                                            onChange={(e) => {
                                              const newBtns = [...template.components.find(c => c.type === 'BUTTONS').buttons];
                                              newBtns[idx] = { ...btn, example: [e.target.value] };
                                              updateComponent('BUTTONS', { buttons: newBtns });
                                            }}
                                            placeholder="e.g. 4821"
                                            className="w-full bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg px-3 py-2 text-[13px] font-medium outline-none focus:border-[#0176D3] transition-all"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {btn.type === 'PHONE_NUMBER' && (
                                    <div>
                                      <label className="text-[10px] font-bold text-[#706E6B] mb-1 block">Phone Number (with country code)</label>
                                      <input
                                        type="text"
                                        value={btn.phone_number || ''}
                                        onChange={(e) => {
                                          const newBtns = [...template.components.find(c => c.type === 'BUTTONS').buttons];
                                          newBtns[idx] = { ...btn, phone_number: e.target.value };
                                          updateComponent('BUTTONS', { buttons: newBtns });
                                        }}
                                        placeholder="+919876543210"
                                        className="w-full bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg px-3 py-2 text-[13px] font-medium outline-none focus:border-[#0176D3] transition-all"
                                      />
                                    </div>
                                  )}
                               </div>
                               <button
                                onClick={() => {
                                  const newBtns = template.components.find(c => c.type === 'BUTTONS').buttons.filter((_, i) => i !== idx);
                                  updateComponent('BUTTONS', { buttons: newBtns });
                                }}
                                className="p-3 text-[#706E6B] hover:text-[#EA001E] hover:bg-[#FEE8E8] rounded-lg transition-all"
                               >
                                <Trash2 size={20} />
                               </button>
                             </Card>
                           ))}
                         </div>
                      </div>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* Right Panel: Preview */}
              <div className="w-full sm:w-[500px] bg-[#F4F6F9] p-12 flex flex-col items-center shrink-0">
                <div className="w-full flex items-center justify-between mb-10">
                   <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#04844B]" />
                    <h3 className="text-[12px] font-bold text-[#080707] uppercase tracking-[0.2em]">Signal Preview</h3>
                   </div>
                   <div className="flex items-center gap-2 text-[#706E6B]">
                    <Smartphone size={16} />
                    <span className="text-[11px] font-bold uppercase tracking-widest">WhatsApp Engine</span>
                   </div>
                </div>

                {/* Phone Mockup */}
                <div className="w-full max-w-[340px] bg-[#0B141A] rounded-[55px] p-5 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] relative border-[6px] border-[#1F2C33]">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-[#1F2C33] rounded-b-lg z-10 shadow-inner" />

                  <div className="bg-[#E5DDD5] h-[600px] rounded-[38px] overflow-hidden flex flex-col relative" style={{ backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")' }}>
                    <div className="flex-1 overflow-y-auto p-5 flex flex-col justify-end no-scrollbar">

                      {/* WhatsApp Message Bubble */}
                      <div className="bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] overflow-hidden max-w-[92%] relative animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="absolute -left-2 top-0 w-5 h-5 bg-white" style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }} />

                        {/* Header Content */}
                        {template.components.find(c => c.type === 'HEADER') && (
                          <div className="p-4 pb-0">
                            {template.components.find(c => c.type === 'HEADER').format === 'TEXT' ? (
                              <p className="font-bold text-[15px] text-[#080707] tracking-tight">{template.components.find(c => c.type === 'HEADER').text || 'Header Identity'}</p>
                            ) : (
                              <div className="bg-[#F4F6F9] rounded-lg aspect-video flex flex-col items-center justify-center gap-3 border border-[#D8DDE6]/50">
                                <div className="p-3 bg-white rounded-full shadow-sm">
                                  {template.components.find(c => c.type === 'HEADER')?.format === 'IMAGE' ? <Image size={24} className="text-[#0176D3]" /> : <FileText size={24} className="text-[#0176D3]" />}
                                </div>
                                <span className="text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Media Component</span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Body Content */}
                        <div className="p-4 pt-3">
                          <p className="text-[14px] text-[#080707] leading-relaxed font-medium">
                            {template.category === 'AUTHENTICATION' ? (
                              <>
                                <span className="bg-[#D6EAFB] text-[#0176D3] px-1 rounded font-bold">{'{{1}}'}</span>
                                {' is your verification code.'}
                                {template.components.find(c => c.type === 'BODY')?.add_security_recommendation && ' For your security, do not share this code.'}
                              </>
                            ) : (
                              (template.components.find(c => c.type === 'BODY')?.text || 'Architect your message body…').split(/(\{\{\d+\}\})/).map((part, i) => (
                                part.match(/\{\{\d+\}\}/)
                                  ? <span key={i} className="bg-[#D6EAFB] text-[#0176D3] px-1 rounded font-bold">{part}</span>
                                  : part
                              ))
                            )}
                          </p>
                        </div>

                        {/* Footer Content */}
                        {template.components.find(c => c.type === 'FOOTER') && (
                          <div className="px-4 pb-4">
                            <p className="text-[12px] text-[#706E6B] font-medium">
                              {template.category === 'AUTHENTICATION'
                                ? `This code expires in ${template.components.find(c => c.type === 'FOOTER')?.code_expiration_minutes ?? 10} minutes.`
                                : (template.components.find(c => c.type === 'FOOTER').text || 'Footer metadata text')}
                            </p>
                          </div>
                        )}

                        {/* Metadata Time */}
                        <div className="px-4 pb-2.5 flex justify-end">
                          <div className="flex items-center gap-1">
                             <span className="text-[10px] text-[#706E6B] font-bold">14:20</span>
                             <div className="flex text-[#0176D3]"><ChevronRight size={10} /><ChevronRight size={10} className="-ml-1" /></div>
                          </div>
                        </div>

                        {/* Buttons Grid */}
                        {template.components.find(c => c.type === 'BUTTONS')?.buttons?.length > 0 && (
                          <div className="border-t border-[#EEF1F6] flex flex-col divide-y divide-[#EEF1F6]">
                            {template.components.find(c => c.type === 'BUTTONS').buttons.map((btn, i) => (
                              <div key={i} className="py-3.5 flex items-center justify-center gap-2 text-[#00A884] font-bold text-[14px] hover:bg-[#F4F6F9] transition-all cursor-pointer">
                                {btn.type === 'URL' && <MousePointer2 size={16} />}
                                {btn.type === 'PHONE_NUMBER' && <Smartphone size={16} />}
                                {btn.text}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                    </div>
                  </div>
                </div>

                <div className="mt-12 p-6 bg-white rounded-lg border border-[#D8DDE6] shadow-[0_1px_3px_rgba(0,0,0,0.08)] max-w-[340px]">
                  <p className="text-[11px] text-[#706E6B] font-medium text-center leading-relaxed">
                    Signal visualization is representative. Final render is controlled by the WhatsApp Cloud Engine and recipient device theme.
                  </p>
                </div>
              </div>
            </>
          ) : (
            /* Library View */
            <div className="flex-1 bg-white p-12 overflow-y-auto no-scrollbar">
              <div className="max-w-5xl mx-auto">
                <div className="flex items-center justify-between mb-10">
                  <div>
                    <h3 className="text-2xl font-bold text-[#080707] tracking-tight">Predefined Blueprints</h3>
                    <p className="text-[14px] text-[#706E6B] font-medium mt-1">Accelerate your workflow with industry-standard messaging patterns.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {libraryTemplates.map((lib, i) => (
                    <Card key={i} className="p-0 overflow-hidden border-[#D8DDE6] shadow-sm hover:shadow-[0_2px_8px_rgba(0,0,0,0.16)] hover:border-[#0176D3]/30 transition-all group flex flex-col">
                      <div className="p-6 flex-1 flex flex-col">
                         <div className="flex items-center justify-between mb-4">
                           <span className={cn(
                             "px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest",
                             lib.category === 'MARKETING' ? "bg-[#FFF4E5] text-[#8C4B02]" : "bg-[#D6EAFB] text-[#0176D3]"
                           )}>{lib.category}</span>
                           <Book size={18} className="text-[#D8DDE6] group-hover:text-[#0176D3] transition-colors" />
                         </div>
                         <h4 className="text-[16px] font-bold text-[#080707] mb-3 group-hover:text-[#0176D3] transition-colors uppercase tracking-tight">{lib.name.replace(/_/g, ' ')}</h4>
                         <div className="bg-[#F4F6F9] p-4 rounded-lg flex-1 mb-6 border border-[#EEF1F6]">
                            <p className="text-[12px] text-[#706E6B] line-clamp-4 leading-relaxed font-medium italic">
                              {lib.components.find(c => c.type === 'BODY')?.text}
                            </p>
                         </div>
                         <Button
                          className="w-full h-11 rounded-lg text-[12px] font-bold uppercase tracking-widest"
                          onClick={() => useLibraryTemplate(lib)}
                         >
                           Deploy Blueprint
                         </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="h-24 px-12 flex items-center justify-between bg-white border-t border-[#D8DDE6] shrink-0">
          <Button variant="outline" className="h-12 px-8 rounded-lg border-[#D8DDE6]" onClick={onClose}>Discard Sequence</Button>
          <div className="flex items-center gap-8">
             <div className="text-right hidden md:block border-r pr-8 border-[#D8DDE6]">
              <p className="text-[12px] font-bold text-[#080707] uppercase tracking-[0.2em]">Meta Cloud Review</p>
              <p className="text-[11px] text-[#706E6B] font-medium mt-1">Approval Typically: <span className="text-[#0176D3] font-bold">~120 Minutes</span></p>
            </div>
            <Button
              onClick={handleSave}
              loading={loading}
              className="h-14 px-12 rounded-lg font-bold uppercase tracking-widest text-[12px] shadow-[0_2px_8px_rgba(0,0,0,0.16)]  bg-[#0176D3] hover:bg-[#0164B5] transition-all"
            >
              <Save className="w-5 h-5 mr-3" />
              {editMode ? 'Save & Re-submit for Review' : 'Finalize & Submit to Meta'}
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default MetaTemplateBuilder;
